import { useRef, useCallback } from 'react';

export function useLongPress(
  onLongPress: () => void,
  onClick?: () => void,
  delay = 600
) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressTriggered = useRef(false);
  const wasMoved = useRef(false);
  const startPosition = useRef<{ x: number; y: number } | null>(null);
  const ignoreMouse = useRef(false);

  const start = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    isLongPressTriggered.current = false;
    wasMoved.current = false;

    if ('touches' in event && event.touches.length > 0) {
      const touch = event.touches[0];
      startPosition.current = { x: touch.clientX, y: touch.clientY };
      ignoreMouse.current = true; // block mouse events after touch
    } else if ('clientX' in event) {
      if (ignoreMouse.current) return; // skip synthetic mouse
      startPosition.current = { x: event.clientX, y: event.clientY };
    }

    timerRef.current = setTimeout(() => {
      onLongPress();
      isLongPressTriggered.current = true;
    }, delay);
  }, [onLongPress, delay]);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const end = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    cancel();

    if ('touches' in event || 'changedTouches' in event) {
      ignoreMouse.current = true;
    } else if ('clientX' in event) {
      if (ignoreMouse.current) {
        ignoreMouse.current = false;
        return;
      }
    }

    if (!isLongPressTriggered.current && !wasMoved.current && onClick) {
      onClick();
    }
  }, [cancel, onClick]);

  const move = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    if (!startPosition.current) return;

    let currentX = 0;
    let currentY = 0;

    if ('touches' in event && event.touches.length > 0) {
      currentX = event.touches[0].clientX;
      currentY = event.touches[0].clientY;
    } else if ('clientX' in event) {
      currentX = event.clientX;
      currentY = event.clientY;
    }

    const dx = currentX - startPosition.current.x;
    const dy = currentY - startPosition.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > 10) {
      wasMoved.current = true;
      cancel();
    }
  }, [cancel]);

  return {
    onMouseDown: (e: React.MouseEvent) => start(e),
    onMouseUp: (e: React.MouseEvent) => end(e),
    onMouseLeave: () => cancel(),
    onMouseMove: (e: React.MouseEvent) => move(e),
    onTouchStart: (e: React.TouchEvent) => start(e),
    onTouchEnd: (e: React.TouchEvent) => end(e),
    onTouchMove: (e: React.TouchEvent) => move(e),
    onTouchCancel: () => cancel(),
  };
}
